<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { VLoading, VModal, VIcon, VButton } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import AccreditationBadge from './AccreditationBadge.vue'
import AccreditationForm from '@/modules/screening/component/AccreditationForm.vue'
import { useAccreditationStore } from '@/modules/screening/stores/accreditation-store'
import { AccreditationStatus } from '../types'
const authStore = useAuthStore()
const route = useRoute()
const accreditationForm = ref(null)

const props = defineProps<{
  screening_accreditation_id: string
  status: AccreditationStatus
  can_edit: boolean
  callback?: any
}>()

const { slug } = route.params as { slug: string }
const isLoading = ref(false)
const modal = useModal()
const inFormMode = ref(false)
const accreditationStore = useAccreditationStore()

const fetchAndOpenModal = async () => {
  isLoading.value = true
  await accreditationStore.getAccreditation(props.screening_accreditation_id, { slug })
  isLoading.value = false
  modal.open()
}

const submit = async (payload) => {
  await accreditationStore.updateResponse(props.screening_accreditation_id, payload, { slug })
  await accreditationStore.getAccreditation(props.screening_accreditation_id, { slug })
  if (props.callback) await props.callback()
  modal.close()
  inFormMode.value = false
}

const editForm = () => {
  inFormMode.value = true
}

const cancelEdit = () => {
  if (accreditationForm.value) accreditationForm.value.resetForm()
  inFormMode.value = false
}

watch(accreditationForm, (form) => {
  if (form) form.resetForm()
})
</script>

<template>
  <button @click="fetchAndOpenModal" class="relative cursor-pointer" :disabled="isLoading">
    <div :class="isLoading ? 'opacity-50' : ''">
      <AccreditationBadge :status="status" />
    </div>

    <template v-if="isLoading">
      <div class="absolute inset-0 flex items-center justify-center">
        <VLoading />
      </div>
    </template>
  </button>

  <VModal :modalStore="modal" :has_footer="false">
    <template #main>
      <AccreditationForm
        ref="accreditationForm"
        :skeleton="isLoading"
        :accreditation="accreditationStore.screeningAccreditation"
        :inDisplayMode="!inFormMode"
        :onSubmit="submit"
      >
        <template #actions v-if="can_edit">
          <VButton variant="v-white" @click="editForm" v-if="!inFormMode">
            <div class="flex cursor-pointer items-center gap-2 stroke-[#3b88af] text-sm">
              Edit
              <VIcon name="Pencil" />
            </div>
          </VButton>

          <VButton variant="v-white" @click="cancelEdit" v-if="inFormMode">
            <div class="flex cursor-pointer items-center gap-2 stroke-[#3b88af] text-sm">
              Cancel
              <VIcon name="X" />
            </div>
          </VButton>
        </template>
      </AccreditationForm>
    </template>
  </VModal>
</template>
